/**
 * B2B endpoint documentation — shared domain types.
 *
 * One `EndpointDoc` per documented Endpoint (or guide page) in Mongo
 * `tipranks_admin.b2b_endpoint_docs`. The shape is **redesigned for us**, not the
 * WordPress snapshot shape it is seeded from: the legacy feed had no verb, no link to
 * a `FeatureUrl` id, no draft/published state and no path params, so the docs site
 * hardcoded `GET` and the sidebar silently hid 26 entries.
 *
 * `Catalog` is the published projection the docs app (`apps/api-docs`, STORY-2106)
 * reads: only `status: "published" && visibleOnDocs`, with `body` pre-rendered to
 * sanitised HTML so the frontend carries no Markdown renderer.
 *
 * Spec: `docs/migration/target/b2b-admin.md` §2.6.1.
 */

export const DOC_STATUSES = ["draft", "published", "deprecated"] as const;
export type DocStatus = (typeof DOC_STATUSES)[number];

export const DOC_KINDS = ["endpoint", "guide"] as const;
export type DocKind = (typeof DOC_KINDS)[number];

export const HTTP_METHODS = ["GET", "POST"] as const;
export type HttpMethod = (typeof HTTP_METHODS)[number];

export const PARAM_TYPES = ["string", "number", "boolean", "date", "enum", "array"] as const;
export type ParamType = (typeof PARAM_TYPES)[number];

export const SAMPLE_LANGS = ["curl", "js", "python", "java", "go"] as const;
export type SampleLang = (typeof SAMPLE_LANGS)[number];

export interface Param {
  name: string;
  type: ParamType;
  required: boolean;
  description: string;
  default?: string;
  /** Present when `type === "enum"`; the legacy `availableOptions` strings, kept verbatim. */
  enum?: string[];
  example?: string;
}

export interface Field {
  name: string;
  type: string;
  description: string;
  nullable?: boolean;
  /** Nested object/array shape, when the feed described one. */
  fields?: Field[];
}

export interface DocCategory {
  /** Slugified category name — the first URL segment on the docs site. */
  id: string;
  name: string;
  order: number;
}

export interface EndpointDoc {
  /** Slug — stable, and the endpoint's URL segment on the docs site. */
  _id: string;
  kind: DocKind;
  /** `FeatureUrl.FeatureUrlID` — the permission this doc describes. Null for guides. */
  featureUrlId: number | null;
  method: HttpMethod;
  /** `/api/stocks/marketMovers`, or `/api/HedgeFunds/bestPerformingExperts/{num}`. Empty for guides. */
  path: string;
  title: string;
  /** One line, for the list, the sidebar and the SEO description. */
  summary: string;
  /** Markdown. The legacy HTML is converted once, at seed. */
  body: string;
  category: DocCategory;
  /** SQL `Product` names this endpoint belongs to; denormalised at publish. */
  products: string[];
  planHint?: "standard" | "enterprise";
  pathParams: Param[];
  queryParams: Param[];
  /** `X-APIKey` / `X-APIToken` are implied for every endpoint; only extras go here. */
  headers?: Param[];
  response: { fields: Field[]; example?: unknown; contentType?: string };
  errors?: { status: number; when: string }[];
  /** Overrides; the docs site generates defaults per language. */
  examples?: { lang: SampleLang; code: string }[];
  status: DocStatus;
  /** Slug of the doc that replaces this one. */
  deprecatedBy?: string;
  visibleOnDocs: boolean;
  seo?: { title?: string; description?: string; noindex?: boolean };
  /** The legacy `/api-docs/<slug>` segment, kept so the cutover can emit its 301. */
  legacySlug?: string;
  updatedAt: Date;
  updatedBy: string;
  publishedAt?: Date;
  /** 1 while untouched since the seed — the seed only overwrites version-1 documents. */
  version: number;
}

// ── The published projection ────────────────────────────────────

/** An `EndpointDoc` as the docs site sees it: body pre-rendered, editorial fields dropped. */
export interface CatalogEntry
  extends Omit<
    EndpointDoc,
    "_id" | "body" | "status" | "visibleOnDocs" | "updatedAt" | "updatedBy" | "version"
  > {
  slug: string;
  /** Sanitised HTML, rendered from `body` at publish time. */
  html: string;
}

export interface Catalog {
  generatedAt: string;
  version: number;
  categories: DocCategory[];
  guides: CatalogEntry[];
  endpoints: CatalogEntry[];
  /**
   * `/api-docs/<legacySlug>` → the new path, for the 83 legacy URLs the cutover
   * (TASK-0396) must keep alive. Generated here rather than hand-maintained in the docs
   * app: the mapping is a property of the documents, and a redirect list that drifts
   * from them is how a migrated site loses its rankings.
   */
  redirects: Record<string, string>;
}

/** Where a document lives on the docs site. Guides sit at the root; endpoints under their category. */
export function docsPathFor(entry: {
  kind: DocKind;
  slug: string;
  category: { id: string };
}): string {
  return entry.kind === "guide" ? `/${entry.slug}` : `/${entry.category.id}/${entry.slug}`;
}

/** Validate a built catalog before it is uploaded. Returns the problems; empty means valid. */
export function validateCatalog(catalog: unknown): string[] {
  const errors: string[] = [];
  const c = catalog as Partial<Catalog> | null;
  if (!c || typeof c !== "object") return ["not an object"];

  if (typeof c.generatedAt !== "string" || Number.isNaN(Date.parse(c.generatedAt))) {
    errors.push("generatedAt: not an ISO timestamp");
  }
  if (typeof c.version !== "number" || c.version < 1) errors.push("version: missing");
  if (!Array.isArray(c.categories)) errors.push("categories: not an array");
  if (!Array.isArray(c.guides)) errors.push("guides: not an array");
  if (!Array.isArray(c.endpoints)) errors.push("endpoints: not an array");
  if (!c.redirects || typeof c.redirects !== "object") errors.push("redirects: not an object");
  if (errors.length > 0) return errors;

  const categoryIds = new Set(c.categories!.map((cat) => cat.id));
  const seen = new Set<string>();

  for (const [label, entries] of [
    ["guides", c.guides!],
    ["endpoints", c.endpoints!],
  ] as const) {
    entries.forEach((entry, i) => {
      const at = `${label}[${i}]`;
      if (typeof entry.slug !== "string" || !entry.slug) errors.push(`${at}.slug: missing`);
      else if (seen.has(entry.slug)) errors.push(`${at}.slug: "${entry.slug}" appears twice`);
      else seen.add(entry.slug);

      if (typeof entry.title !== "string" || !entry.title) errors.push(`${at}.title: missing`);
      if (typeof entry.html !== "string") errors.push(`${at}.html: missing`);
      // A body that still looks like Markdown means the pre-render did not run, and the
      // docs site has no renderer to fall back on — readers would see the asterisks.
      if (typeof entry.html === "string" && /\*\*[^*]+\*\*/.test(entry.html)) {
        errors.push(`${at}.html: still contains Markdown emphasis — it was not rendered`);
      }
      if (!entry.category || !categoryIds.has(entry.category.id)) {
        errors.push(`${at}.category: "${entry.category?.id}" is not in categories[]`);
      }
      if (label === "endpoints" && (typeof entry.path !== "string" || !entry.path.startsWith("/"))) {
        errors.push(`${at}.path: not an absolute path`);
      }
    });
  }

  for (const [from, to] of Object.entries(c.redirects!)) {
    if (!from.startsWith("/")) errors.push(`redirects: "${from}" is not an absolute path`);
    if (typeof to !== "string" || !to.startsWith("/")) {
      errors.push(`redirects["${from}"]: "${String(to)}" is not an absolute path`);
    }
  }

  return errors;
}

// ── Helpers ─────────────────────────────────────────────────────

/** Slugify a category name into its URL segment: `Analyst Ratings & Forecasts` → `analyst-ratings-forecasts`. */
export function slugifyCategory(name: string): string {
  return name
    .toLowerCase()
    .replace(/&(amp|#38);/g, " ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * The two WordPress categories the legacy sidebar dropped. Their 26 entries were
 * reachable only by direct URL; they seed as `draft` so an editor assigns a real one.
 */
export const HIDDEN_LEGACY_CATEGORIES = ["Uncategorized", "Miscellaneous"];

export function isHiddenLegacyCategory(name: string): boolean {
  return HIDDEN_LEGACY_CATEGORIES.includes(decodeHtmlEntities(name).trim());
}

/** The handful of entities the WordPress feed emits. Not a general HTML decoder. */
export function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&#8211;/g, "–")
    .replace(/&#8212;/g, "—")
    .replace(/&#8217;/g, "’")
    .replace(/&#8216;/g, "‘")
    .replace(/&#821[89];/g, '"')
    .replace(/&#8230;/g, "…")
    .replace(/&nbsp;/g, " ")
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
}

/** `{ticker}` segments in a path, in order. */
export function extractPathParamNames(path: string): string[] {
  return [...path.matchAll(/\{([^}]+)\}/g)].map((m) => m[1]);
}

// ── Validation ──────────────────────────────────────────────────
//
// Hand-written rather than schema-library-driven: nothing else in `apps/admin` carries
// a runtime validator dependency, and the seed needs a *report* of everything wrong
// with an entry rather than a throw on the first problem.

function checkParam(value: unknown, path: string, errors: string[]): void {
  const p = value as Partial<Param> | null;
  if (!p || typeof p !== "object") {
    errors.push(`${path}: not an object`);
    return;
  }
  if (typeof p.name !== "string" || !p.name) errors.push(`${path}.name: missing`);
  if (typeof p.description !== "string") errors.push(`${path}.description: missing`);
  if (typeof p.required !== "boolean") errors.push(`${path}.required: missing`);
  if (!PARAM_TYPES.includes(p.type as ParamType)) errors.push(`${path}.type: ${String(p.type)}`);
  if (p.type === "enum" && (!Array.isArray(p.enum) || p.enum.length === 0)) {
    errors.push(`${path}.enum: an enum param needs options`);
  }
}

function checkField(value: unknown, path: string, errors: string[]): void {
  const f = value as Partial<Field> | null;
  if (!f || typeof f !== "object") {
    errors.push(`${path}: not an object`);
    return;
  }
  if (typeof f.name !== "string" || !f.name) errors.push(`${path}.name: missing`);
  if (typeof f.type !== "string" || !f.type) errors.push(`${path}.type: missing`);
  if (typeof f.description !== "string") errors.push(`${path}.description: missing`);
  if (f.fields !== undefined) {
    if (!Array.isArray(f.fields)) errors.push(`${path}.fields: not an array`);
    else f.fields.forEach((child, i) => checkField(child, `${path}.fields[${i}]`, errors));
  }
}

/** Validate a document about to be written. Returns the problems; empty means valid. */
export function validateEndpointDoc(doc: unknown): string[] {
  const errors: string[] = [];
  const d = doc as Partial<EndpointDoc> | null;
  if (!d || typeof d !== "object") return ["not an object"];

  if (typeof d._id !== "string" || !/^[a-z0-9][a-z0-9-]*$/.test(d._id)) {
    errors.push(`_id: ${String(d._id)} is not a slug`);
  }
  if (!DOC_KINDS.includes(d.kind as DocKind)) errors.push(`kind: ${String(d.kind)}`);
  if (!DOC_STATUSES.includes(d.status as DocStatus)) errors.push(`status: ${String(d.status)}`);
  if (!HTTP_METHODS.includes(d.method as HttpMethod)) errors.push(`method: ${String(d.method)}`);
  if (typeof d.title !== "string" || !d.title) errors.push("title: missing");
  if (typeof d.summary !== "string") errors.push("summary: missing");
  if (typeof d.body !== "string") errors.push("body: missing");
  if (typeof d.visibleOnDocs !== "boolean") errors.push("visibleOnDocs: missing");
  if (typeof d.version !== "number" || d.version < 1) errors.push("version: missing");
  if (d.featureUrlId !== null && typeof d.featureUrlId !== "number") {
    errors.push("featureUrlId: must be a number or null");
  }
  if (!d.category || typeof d.category.id !== "string" || !d.category.id) {
    errors.push("category.id: missing");
  }
  if (!Array.isArray(d.products)) errors.push("products: missing");

  if (d.kind === "endpoint") {
    if (typeof d.path !== "string" || !d.path.startsWith("/")) {
      errors.push(`path: ${String(d.path)} is not an absolute path`);
    } else {
      // A `{param}` in the path with no matching `pathParams` entry is exactly how the
      // legacy docs shipped `{ticker}` placeholders nobody could fill in.
      const declared = new Set((d.pathParams ?? []).map((p) => p.name));
      for (const name of extractPathParamNames(d.path)) {
        if (!declared.has(name)) errors.push(`pathParams: {${name}} in path is undeclared`);
      }
    }
  }

  for (const key of ["pathParams", "queryParams", "headers"] as const) {
    const list = d[key];
    if (list === undefined && key === "headers") continue;
    if (!Array.isArray(list)) {
      errors.push(`${key}: not an array`);
      continue;
    }
    list.forEach((p, i) => checkParam(p, `${key}[${i}]`, errors));
  }

  if (!d.response || !Array.isArray(d.response.fields)) errors.push("response.fields: not an array");
  else d.response.fields.forEach((f, i) => checkField(f, `response.fields[${i}]`, errors));

  return errors;
}
